import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/Add';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { listColleges, createCollege, updateCollege } from '../services/adminApi';

const EMPTY_FORM = { name: '', location: '', contact_email: '', contact_name: '', phone: '', notes: '' };

export default function CollegesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchColleges = useCallback(() => {
    setLoading(true);
    listColleges()
      .then((data) => setColleges(data.items || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchColleges();
  }, [fetchColleges]);

  const handleOpen = (college = null) => {
    if (college) {
      setEditing(college);
      setForm({
        name: college.name || '',
        location: college.location || '',
        contact_email: college.contact_email || '',
        contact_name: college.contact_name || '',
        phone: college.phone || '',
        notes: college.notes || '',
      });
    } else {
      setEditing(null);
      setForm(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await updateCollege(editing.id || editing.college_id, form);
      } else {
        await createCollege(form);
      }
      setDialogOpen(false);
      fetchColleges();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1.5, minWidth: 200 },
    { field: 'location', headerName: 'Location', flex: 1, minWidth: 150 },
    { field: 'contact_email', headerName: 'Contact Email', flex: 1, minWidth: 200 },
    { field: 'contact_name', headerName: 'Contact Name', width: 160, valueGetter: (params) => params.row.contact_name || '-' },
    { field: 'intern_count', headerName: 'Interns', width: 100, valueGetter: (params) => params.row.intern_count ?? '-' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Colleges</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add College
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && colleges.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No colleges yet.</Typography>
            </Paper>
          )}
          {!loading && colleges.map((c) => (
            <Paper
              key={c.id || c.college_id || c.name}
              sx={{ p: 2, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
              onClick={() => handleOpen(c)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{c.name || '—'}</Typography>
              {c.location && (
                <Typography variant="body2" color="text.secondary" noWrap>{c.location}</Typography>
              )}
              {(c.contact_name || c.contact_email) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {c.contact_name}{c.contact_name && c.contact_email && ' · '}{c.contact_email}
                </Typography>
              )}
              {c.phone && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {c.phone}
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      ) : (
        <Box sx={{ height: 600, backgroundColor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={colleges}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id || row.college_id || row.name}
            onRowClick={(params) => handleOpen(params.row)}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            disableRowSelectionOnClick
          />
        </Box>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit College' : 'Add College'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="College Name"
              value={form.name}
              onChange={handleFieldChange('name')}
              fullWidth
              required
            />
            <TextField
              label="Location"
              value={form.location}
              onChange={handleFieldChange('location')}
              fullWidth
            />
            <TextField
              label="Contact Email"
              type="email"
              value={form.contact_email}
              onChange={handleFieldChange('contact_email')}
              fullWidth
            />
            <TextField
              label="Contact Name"
              value={form.contact_name}
              onChange={handleFieldChange('contact_name')}
              fullWidth
            />
            <TextField
              label="Phone"
              value={form.phone}
              onChange={handleFieldChange('phone')}
              fullWidth
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={handleFieldChange('notes')}
              fullWidth
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={!form.name || saving}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
